import { createApiRef, FetchApi, DiscoveryApi } from '@backstage/core-plugin-api';

export interface GCPSubnetData {
  [businessUnit: string]: {
    [region: string]: {
      [subnetType: string]: string[] | {
        names: string[];
        fullPaths: string[];
        details: Array<{
          name: string;
          fullPath: string;
          network: string;
          ipCidrRange: string;
          region: string;
        }>;
      };
    };
  };
}

export interface SimpleGCPSubnetSelectorApi {
  getTemplateSubnets(businessUnit: string, region: string, env?: string): Promise<GCPSubnetData>;
  testConnection(): Promise<boolean>;
}

export const simpleGCPSubnetSelectorApiRef = createApiRef<SimpleGCPSubnetSelectorApi>({
  id: 'plugin.gcp-subnet-selector.simple',
});

export class SimpleGCPSubnetSelectorClient implements SimpleGCPSubnetSelectorApi {
  private readonly fetchApi: FetchApi;
  private readonly discoveryApi: DiscoveryApi;

  constructor(fetchApi: FetchApi, discoveryApi: DiscoveryApi) {
    this.fetchApi = fetchApi;
    this.discoveryApi = discoveryApi;
  }

  private async getBaseUrl(): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('google-cloud');
    return baseUrl;
  }

  async testConnection(): Promise<boolean> {
    try {
      const baseUrl = await this.getBaseUrl();
      console.log('Simple GCP API: Testing connection to', `${baseUrl}/health`);
      const response = await this.fetchApi.fetch(`${baseUrl}/health`);
      console.log('Simple GCP API: Health check response:', response.status, response.ok);
      const data = await response.json();
      console.log('Simple GCP API: Health check data:', data);
      return response.ok;
    } catch (error) {
      console.error('Simple GCP connection test failed:', error);
      return false;
    }
  }

  async getTemplateSubnets(businessUnit: string, region: string, env?: string): Promise<GCPSubnetData> {
    try {
      const baseUrl = await this.getBaseUrl();
      const url = new URL(`${baseUrl}/subnets/for-template`);
      url.searchParams.append('business_unit', businessUnit);
      url.searchParams.append('region', region);
      if (env) url.searchParams.append('env', env);

      console.log('Simple GCP API: Fetching template subnets from:', url.toString());
      const response = await this.fetchApi.fetch(url.toString());
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch template subnets: ${response.statusText} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Simple GCP API: Received template subnets:', data);
      return data;
    } catch (error) {
      console.error('Error fetching GCP template subnets:', error);
      throw error;
    }
  }
}
